// Package imports:
import React, { useState, useEffect } from 'react';
import { SwiperSlide } from 'swiper/react';
// Component imports:
import Button from '../../ui-elements/Button/Button';
import PriceCard from '../PriceCard/PriceCard';
import Slider from '../Slider/Slider';
import FullScreenImage from '../../ui-elements/FullScreenImage/FullScreenImage';
import HeadSection from '../../ui-elements/HeadSection/HeadSection';
import DueDiligenceForm from '../ContactForms/DueDiligenceForm/DueDiligenceForm';
import Link from '../../ui-elements/Link/Link';
import LogoMarquee from '../../ui-elements/LogoMarquee/LogoMarquee';
// Service imports:
import { getSubscriptionPrice } from '../../services/utils';
import Tooltip from '../../ui-elements/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faCheck } from '@fortawesome/pro-solid-svg-icons';
import { GET_STAGING_STRING } from '../../services/config';

const AREIDANLEIKAKANNANIR_IMAGES_URLS = [
    "https://cdn.livemarketdata.com/KeldanImages/kyc-kerfi-1.png",
    "https://cdn.livemarketdata.com/KeldanImages/kyc-kerfi-2.png",
    "https://cdn.livemarketdata.com/KeldanImages/kyc-kerfi-3.png",
    "https://cdn.livemarketdata.com/KeldanImages/kyc-kerfi-4.png",
];

const FEATURE_ITEMS: JSX.Element[] = [
    <span>Áreiðanleikakannanir á <strong>lögaðilum</strong> og <strong>einstaklingum</strong>.</span>,
    <span>Áreiðanleikakannanir á <strong>erlendum aðilum</strong>.</span>,
    <span><strong>Gildandi skráning með raunverulegum eigendum</strong>, sótt sjálfkrafa frá Hlutafélagaskrá.</span>,
    // <span>Sjálfvirk athugun <strong>orðsporsáhættu</strong> (fjölmiðlar og dómar).</span>,
    <span>Sjálfvirk leit á íslenskum <strong>PEP lista Keldunnar</strong>.</span>,
    <span>Sjálfvirk leit á móti <strong>alþjóðlegum válistum</strong>.</span>,
    <span><strong>Ríkisfang</strong> einstaklinga keyrt á móti áhættu- og ósamvinnuþýðum ríkjum.</span>,
    <span><strong>Spurningalisti</strong> sendur út og svör við spurningalista móttekin.</span>,
    <span><strong>Rafræn innskráning</strong> viðskiptavina (framvísun persónuskilríkja).</span>,
    <span>Skráning <strong>áhættumats</strong> handvirkt eða sjálfvirkt.</span>,
    <span>Skjöl hengd við hverja áreiðanleikakönnun (<strong>aukin áreiðanleikakönnun</strong>).</span>,
    <span><strong>Sjálfvirk áminning</strong> þegar ákveðinn tími er liðinn frá síðustu könnun.</span>,
    <span><strong>Viðvarandi eftirlit</strong> með stöðu viðskiptavina á móti innlendum og erlendum válistum.</span>,
    // <span>Góð <strong>heildstæð yfirsýn</strong> á viðskiptavinum.</span>
];

const COUNT_DURATION = 1000;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const useCountUp = (target: number) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let startTime: number | null = null;
        let rafId: number;

        const animate = (timestamp: number) => {
            if (!startTime) startTime = timestamp;
            const elapsed = timestamp - startTime;
            const progress = Math.min(elapsed / COUNT_DURATION, 1);
            setCount(Math.round(target * easeOutCubic(progress)));
            if (progress < 1) {
                rafId = requestAnimationFrame(animate);
            }
        };

        rafId = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(rafId);
    }, [target]);

    return count;
};
const KYC_COMPANIES = [
    "and-palsson",
    "acro",
    "advant",
    "alma",
    "fastland",
    "fossar",
    "kvika",
    "lex-logmenn",
    "magna",
    "michelsen",
    "sjova",
    "ivera",
    "skattur-and-bokhald"
];

const Areidanleikakannanir: React.FC = () => {
    const [fullScreenImageUrl, setFullScreenImageUrl] = useState<string | null>(null);
    const clients = useCountUp(190);
    const surveys = useCountUp(5000);
    const midpoint = Math.ceil(FEATURE_ITEMS.length / 2);
    return (
        <div className='main KCL_areidanleikakannanir-page'>
            {fullScreenImageUrl && (
                <FullScreenImage
                    imgUrl={fullScreenImageUrl}
                    imgAlt="Fullscreen Áreiðanleikakönnun screenshot"
                    onExit={() => setFullScreenImageUrl(null)}
                />
            )}
            <div className="main__inner-fluid">
                <HeadSection
                    title={<img src='https://cdn.livemarketdata.com/KeldanLogos/keldan-kyc.svg' alt='Keldan KYC' />}
                    summary={
                        <p>
                            Hvíla á þér skyldur um aðgerðir gegn peningaþvætti og fjármögnun hryðjuverka? <br />
                            Keldan býður upp á öflugar lausnir sem aðstoða tilkynningarskylda aðila við að framkvæma áreiðanleikakönnun og viðhafa eftirlit með viðskiptavinum á einfaldan og öruggan hátt.
                        </p>
                    }
                    actions={
                        <>
                            <Button
                                size="lg"
                                buttonType='secondary'
                                anchorProps={{
                                    href: '/Areidanleikakannanir/Yfirlit',
                                }}
                            >
                                Keldan KYC
                            </Button>

                            <Button
                                size="lg"
                                buttonType='primary'
                                anchorProps={{
                                    href: '#kynning',
                                }}
                            >
                                Fá kynningu
                            </Button>
                            <Link icon='angleRight' href={`https://kyc${GET_STAGING_STRING('-')}.keldan.is`}
                            >
                                Svara áreiðanleikakönnun
                            </Link>
                        </>
                    }
                    textAlign='center'
                />
                <div className="section__body">
                    <div className="mini_shell">
                        <div className="stats-section">
                            <div className="stats-section__col">
                                <h2 className="stats-section__title">Áreiðanleiki og yfirsýn</h2>
                                <p className="paragraph">
                                    KYC kerfið („Know your customer") er ætlað til utanumhalds og framkvæmdar áreiðanleikakannana og stenst þau lög sem tilkynningarskyldir aðilar falla undir um aðgerðir gegn peningaþvætti (AML) og fjármögnun hryðjuverka (CTF).
                                    <br />
                                    Kerfið gerir notendum kleift að framkvæma áreiðanleikakannanir á einfaldan hátt og öðlast góða yfirsýn á viðskiptavinum, lögaðilum og einstaklingum.
                                </p>
                            </div>
                            <div className="stats-section__col">
                                <h2 className="stats-section__title">Lausnir sem viðskiptavinir treysta</h2>
                                <div className="stats-section__cards">
                                    <div className="stats-section__card">
                                        <span className="stats-section__number">{clients}+</span>
                                        <span className="stats-section__label">viðskiptavinir</span>
                                    </div>
                                    <div className="stats-section__card">
                                        <span className="stats-section__number">{surveys.toLocaleString('de-DE')}+</span>
                                        <span className="stats-section__label">kannanir á mánuði</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <LogoMarquee
                        logos={KYC_COMPANIES}
                    />
                    <div className="mini_shell">
                        <div className="features-section">
                            <h2 className="features-section__title">Góð heildstæð yfirsýn á viðskiptavinum</h2>
                            <div className="features-section__columns">
                                <div className="features-section__col">
                                    {FEATURE_ITEMS.slice(0, midpoint).map((item, i) => (
                                        <div className="features-section__item" key={i}>
                                            <FontAwesomeIcon icon={faCheck} className="features-section__icon" />
                                            {item}
                                        </div>
                                    ))}
                                </div>
                                <div className="features-section__col">
                                    {FEATURE_ITEMS.slice(midpoint).map((item, i) => (
                                        <div className="features-section__item" key={i}>
                                            <FontAwesomeIcon icon={faCheck} className="features-section__icon" />
                                            {item}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                {/* screenshots */}
                <div className="section section--gray">
                    <div className="section__head">
                        <div className="shell">
                            <h2 id="Vaktarinn_Screenshots">Skjáskot úr KYC kerfinu</h2>
                        </div>
                    </div>
                    <div className="section__body">
                        <div className="shell">
                            <Slider
                                className="slider-screenshots"
                                perView={3}
                                centerSlides={false}
                                loop={true}
                                breakpointOptions={{
                                    320: {
                                        slidesPerView: 1
                                    },
                                    768: {
                                        slidesPerView: 3
                                    }
                                }}
                                onInit={() => { }}
                                onSlideChange={() => { }}
                            >
                                {AREIDANLEIKAKANNANIR_IMAGES_URLS.map((url) => (
                                    <SwiperSlide
                                        key={url}
                                        onClick={() => setFullScreenImageUrl(url)}
                                    >
                                        <img
                                            className="slide-image"
                                            src={url}
                                            alt="Áreiðanleikakannanir kerfi skjáskot"
                                            width="1406"
                                            height="811"
                                        />
                                    </SwiperSlide>
                                ))}
                            </Slider>
                        </div>
                    </div>
                </div>
                <div className="mini_shell AML_voktun">
                    <h2>AML vöktun</h2>
                    <div className="AML_voktun-text">
                        <p className='paragraph'>
                            AML vaktin er þjónusta sem gerir tilkynningarskyldum aðilum kleift að framkvæma reglubundna athugun á viðskiptavinum með sjálfvirkum hætti í gegnum vefþjónustur og/eða vefviðmót Keldunnar.
                        </p>
                        <p className='paragraph'>
                            AML vaktin veitir samfellda vöktun á helstu áhættuþáttum, þar á meðal stjórnmálalegum tengslum (PEP), alþjóðlegum válistum, neikvæðu orðspori, ríkisfangi, gildandi skráningu og raunverulegum eigendum lögaðila.
                        </p>
                        <Button
                            buttonType='secondary'
                            size='lg'
                            anchorProps={{ href: "/Valistar/Leit#Pep-vaktin", style: { marginTop: '20px' } }}>
                            AML vaktin
                        </Button>
                    </div>
                </div>
                {/* //fiorm */}
                <div className="section section--gray section--contact" id="kynning">
                    <div className="shell">
                        <div className="section__inner">
                            <div className="">
                                <h2>Fá kynningu á KYC kerfinu</h2>
                                <p className='summary-paragraph' style={{ maxWidth: '636px' }}>
                                    Hafðu samband ef þú vilt fá aðgang að Keldan KYC eða til að panta kynningu á kerfinu.
                                </p>
                                <DueDiligenceForm />
                            </div>
                            <div className="section__body">
                                <PriceCard
                                    variant="overview"
                                    type="Verðskrá"
                                >
                                    <div className="price-card__details">
                                        <dl>
                                            <dt className='paragraph--bold'>Keldan áskrift</dt>
                                            <dd className='paragraph--bold'>{getSubscriptionPrice(true)}</dd>
                                        </dl>
                                        <p className='paragraph-small--italic' style={{ marginBottom: '15px' }}>
                                            Mánaðargjald
                                        </p>
                                        <dl>
                                            <dt className='paragraph--bold'>Aðgangur að KYC kerfinu</dt>
                                            <dd className='paragraph--bold'>1.990 kr.</dd>
                                        </dl>
                                        <p className='paragraph-small--italic' style={{ marginBottom: '15px' }}>
                                            Mánaðargjald
                                        </p>
                                        <div className='content-wrapper'>
                                            <dl style={{ marginBottom: '15px' }}>
                                                <dt>Áreiðanleikakönnun á lögaðilum</dt>
                                                <dd>1.490 kr.</dd>
                                            </dl>
                                            <dl style={{ marginBottom: '15px' }}>
                                                <dt>Áreiðanleikakönnun á einstaklingum</dt>
                                                <dd>790 kr.</dd>
                                            </dl>
                                        </div>
                                        <dl>
                                            <dt className='paragraph--bold'>Vöktun <Tooltip tooltipClassName='kyc-pricecard-tooltip' noUnderline text={<FontAwesomeIcon icon={faInfoCircle} />} tooltip={<>Vöktun einstaklings á móti PEP (mánaðargjald)  24 kr.<br />Vöktun einstaklings á móti alþjóðlegum válistum (ársfjórðungsgjald)  195 kr.</>} /></dt>
                                            <dd>24 kr.</dd>
                                        </dl>
                                        <p className='paragraph-small--italic' style={{ marginBottom: '15px' }}>
                                            Mánaðargjald
                                        </p>
                                        <p className='paragraph-small--italic'>Athugið, verðin eru án VSK.</p>
                                    </div>
                                </PriceCard>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
}

export default Areidanleikakannanir;